import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  CreditCard, 
  ArrowUpRight, 
  ArrowDownLeft,
  Trash2,
  Edit2,
  Loader2,
  AlertCircle,
  X,
  FileUp,
  History,
  CheckCircle,
  ArrowLeft,
  ChevronRight,
  Download,
  FileText
} from 'lucide-react';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  where,
  getDocs,
  serverTimestamp,
  deleteDoc,
  doc,
  updateDoc,
  Timestamp } from '../firebase';
import { db, auth } from '../firebase';
import ConfirmModal from './ConfirmModal';
import BankStatementUpload from './BankStatementUpload';
import { notify } from '../services/notify';
import { clsx, type ClassValue } from 'clsx';
import { normalizeCurrency } from '../lib/currency';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());
const getLocalApiHeaders = () => ({
  'Authorization': `Bearer ${getLocalAuthToken()}`,
  'Content-Type': 'application/json'
});

interface BankAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  balance: number;
  currency: string;
  nature: 'Debit' | 'Credit';
  createdAt: any;
  source?: 'bank_accounts' | 'chart_of_accounts';
  code?: string;
  chartAccountId?: string;
}

interface BankTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: string;
  reference?: string;
  category?: string;
  matchable?: boolean;
}

interface BankStatement {
  id: string;
  importDate: any;
  fileName: string;
  status: 'pending' | 'completed';
  transactionCount: number;
  matchedCount?: number;
}

interface BankStatementLine {
  id: string;
  date: string;
  amount: number;
  payee: string;
  description: string;
  reference?: string;
  checkNumber?: string;
  status: 'unreconciled' | 'reconciled';
  matchedJournalLineId?: string | null;
}

// Error Info Interface for Firestore
enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: any;
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
}

export default function BankAccountsView({ orgId, language = 'en', initialAccountId, onClearInitial }: { orgId: string, language?: string, initialAccountId?: string, onClearInitial?: () => void }) {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null);
  const [systemSettings, setSystemSettings] = useState({
    defaultCurrency: 'USD',
    allowedCurrencies: [] as string[]
  });
  
  // Detail view state
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [activeTab, setActiveTab] = useState<'transactions' | 'reconciliation' | 'details'>('transactions');
  const [accountTransactions, setAccountTransactions] = useState<BankTransaction[]>([]);
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [reconcilingStatement, setReconcilingStatement] = useState<BankStatement | null>(null);
  const [statementLines, setStatementLines] = useState<BankStatementLine[]>([]);
  const [selectedStatementLineId, setSelectedStatementLineId] = useState<string | null>(null);

  useEffect(() => {
    if (!initialAccountId || selectedAccount || accounts.length === 0) return;
    const account = accounts.find(item => item.id === initialAccountId);
    if (account) {
      setSelectedAccount(account);
      setActiveTab('reconciliation');
    }
  }, [accounts, initialAccountId, selectedAccount]);
  
  // Form state
  const [formData, setFormData] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    balance: 0,
    currency: normalizeCurrency(systemSettings.defaultCurrency),
    nature: 'Debit' as 'Debit' | 'Credit'
  });

  useEffect(() => {
    if (!orgId) return;
    if (isLocalSession()) {
      let cancelled = false;
      void fetch(`/api/organizations/${orgId}/settings`, { headers: getLocalApiHeaders() })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load settings: ${response.status}`);
          return response.json();
        })
        .then(payload => {
          if (cancelled) return;
          const data = { ...(payload?.data || {}), ...(payload || {}) };
          const defaultCurrency = normalizeCurrency(data.defaultCurrency ?? data.default_currency ?? data.currency);
          const allowedCurrencies = data.allowedCurrencies || data.allowed_currencies || [];
          setSystemSettings({ defaultCurrency, allowedCurrencies });
          if (!editingAccount) setFormData(previous => ({ ...previous, currency: defaultCurrency }));
        })
        .catch(error => console.error('Local API error loading settings:', error));
      return () => { cancelled = true; };
    }

    const unsubscribe = onSnapshot(doc(db, 'settings', orgId), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const defaultCurrency = normalizeCurrency(data.defaultCurrency ?? data.default_currency ?? data.currency);
        const allowedCurrencies = data.allowedCurrencies || [];
        setSystemSettings({
          defaultCurrency,
          allowedCurrencies
        });
        // Update default currency in form if not already set or if adding new
        if (!editingAccount) {
          setFormData(prev => ({ ...prev, currency: defaultCurrency }));
        }
      }
    }, (error) => {
      console.error("Error fetching settings:", error);
    });

    return () => unsubscribe();
  }, [editingAccount, orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;

      const loadLocalAccounts = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(`/api/organizations/${orgId}/bank-accounts`, {
            headers: getLocalApiHeaders()
          });
          if (!response.ok) {
            throw new Error(`Failed to load bank accounts: ${response.status}`);
          }

          const localAccounts = await response.json() as BankAccount[];
          if (!cancelled) {
            setAccounts(localAccounts);
          }
        } catch (error) {
          console.error('Local API error loading bank accounts:', error);
          if (!cancelled) {
            setAccounts([]);
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      };

      void loadLocalAccounts();
      return () => {
        cancelled = true;
      };
    }

    const qBank = query(
      collection(db, 'bank_accounts'), 
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    const qCOA = query(
      collection(db, 'chart_of_accounts'), 
      where('orgId', '==', orgId),
      where('type', '==', 'Bank'), 
      where('status', '==', 'active')
    );

    let bankAccounts: BankAccount[] = [];
    let coaAccounts: BankAccount[] = [];

    const updateMergedAccounts = () => {
      // Merge accounts, avoiding duplicates if possible
      // We'll use accountName/name as the primary key for merging if they match exactly
      const merged = [...bankAccounts];
      
      coaAccounts.forEach(coa => {
        const exists = merged.some(b => 
          b.accountName.toLowerCase() === coa.accountName.toLowerCase() || 
          (b.id === coa.id)
        );
        if (!exists) {
          merged.push(coa);
        }
      });

      setAccounts([...merged].sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      }));
      setIsLoading(false);
    };

    const unsubBank = onSnapshot(qBank, (snapshot) => {
      bankAccounts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        source: 'bank_accounts'
      })) as BankAccount[];
      updateMergedAccounts();
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'bank_accounts');
      setIsLoading(false);
    });

    const unsubCOA = onSnapshot(qCOA, (snapshot) => {
      coaAccounts = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          accountName: data.name,
          accountNumber: data.code || '',
          bankName: 'Chart of Accounts',
          balance: data.ytd || 0,
          currency: data.currency || systemSettings.defaultCurrency,
          createdAt: data.createdAt,
          source: 'chart_of_accounts',
          code: data.code
        } as BankAccount;
      });
      updateMergedAccounts();
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts');
    });

    return () => {
      unsubBank();
      unsubCOA();
    };
  }, [systemSettings.defaultCurrency, orgId]);

  useEffect(() => {
    if (!selectedAccount || !orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      void Promise.all([
        fetch(`/api/organizations/${orgId}/bank-accounts/${selectedAccount.id}/transactions`, { headers: getLocalApiHeaders() }),
        fetch(`/api/organizations/${orgId}/bank-accounts/${selectedAccount.id}/statements`, { headers: getLocalApiHeaders() })
      ])
        .then(async ([transactionsResponse, statementsResponse]) => {
          if (!transactionsResponse.ok || !statementsResponse.ok) {
            throw new Error('Failed to load bank account details');
          }
          return Promise.all([transactionsResponse.json(), statementsResponse.json()]);
        })
        .then(([transactions, localStatements]) => {
          if (!cancelled) {
            setAccountTransactions(Array.isArray(transactions) ? transactions : []);
            setStatements(Array.isArray(localStatements) ? localStatements : []);
          }
        })
        .catch(error => {
          if (!cancelled) {
            console.error('Local API error loading bank account details:', error);
            setAccountTransactions([]);
            setStatements([]);
          }
        });
      return () => { cancelled = true; };
    }

    // Fetch transactions for selected account
    const q = query(
      collection(db, 'transactions'),
      where('orgId', '==', orgId),
      where('accountId', '==', selectedAccount.id),
      orderBy('date', 'desc')
    );

    const unsubTransactions = onSnapshot(q, (snapshot) => {
      setAccountTransactions(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BankTransaction[]);
    });

    // Fetch statements for selected account
    const qStatements = query(
      collection(db, 'bank_statements'),
      where('orgId', '==', orgId),
      where('accountId', '==', selectedAccount.id),
      orderBy('importDate', 'desc')
    );

    const unsubStatements = onSnapshot(qStatements, (snapshot) => {
      setStatements(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BankStatement[]);
    });

    return () => {
      unsubTransactions();
      unsubStatements();
    };
  }, [selectedAccount, orgId]);

  useEffect(() => {
    if (!reconcilingStatement) {
      setStatementLines([]);
      setSelectedStatementLineId(null);
      return;
    }

    if (isLocalSession()) {
      let cancelled = false;
      void fetch(`/api/organizations/${orgId}/bank-statements/${reconcilingStatement.id}/lines`, {
        headers: getLocalApiHeaders()
      })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load statement lines: ${response.status}`);
          return response.json();
        })
        .then(lines => {
          if (!cancelled) setStatementLines(Array.isArray(lines) ? lines : []);
        })
        .catch(error => {
          if (!cancelled) {
            console.error('Local API error loading statement lines:', error);
            setStatementLines([]);
          }
        });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'bank_statement_lines'),
      where('statementId', '==', reconcilingStatement.id),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const lines = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStatementLines(lines as BankStatementLine[]);
    });

    return () => unsubscribe();
  }, [reconcilingStatement, orgId]);

  const handleDownloadTemplate = () => {
    const headers = ['Date', 'Amount', 'By', 'Description', 'Reference', 'Check Number'];
    const csvContent = [
      headers.join(','),
      '2026-03-01,-12.50,Starbucks,Coffee,TXN-001,',
      '2026-03-02,1500.00,Client A,Invoice Payment,INV-100,',
      '2026-03-03,-2000.00,Landlord,Monthly Rent,RENT-MAR,102'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bank_statement_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportStatement = async (data?: any) => {
    if (!selectedAccount) return;
    setIsImporting(true);
    try {
      if (isLocalSession()) {
        const response = await fetch(`/api/organizations/${orgId}/bank-accounts/${selectedAccount.id}/statements`, {
          method: 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify({
            fileName: data?.fileName || `statement_${new Date().toISOString().split('T')[0]}.csv`,
            lines: Array.isArray(data?.lines) ? data.lines : []
          })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || `Failed to import statement: ${response.status}`);
        const statement = payload?.statement as BankStatement;
        const lines = Array.isArray(payload?.lines) ? payload.lines as BankStatementLine[] : [];
        if (!statement?.id) throw new Error('Statement import returned no statement');
        setStatements(current => [statement, ...current]);
        setStatementLines(lines);
        setReconcilingStatement(statement);
        setSelectedStatementLineId(null);
        setIsUploadModalOpen(false);
        notify.confirm(`Imported ${lines.length} statement line${lines.length === 1 ? '' : 's'}`);
        return;
      }

      // Create statement record
      const stmtRef = await addDoc(collection(db, 'bank_statements'), {
        orgId,
        accountId: selectedAccount.id,
        importDate: serverTimestamp(),
        fileName: data?.fileName || `statement_${new Date().toISOString().split('T')[0]}.csv`,
        status: 'pending',
        transactionCount: data?.transactionCount || 3,
        createdBy: auth.currentUser?.uid
      });

      const newStatement = {
        id: stmtRef.id,
        orgId,
        accountId: selectedAccount.id,
        importDate: new Date(),
        fileName: data?.fileName || `statement_${new Date().toISOString().split('T')[0]}.csv`,
        status: 'pending' as const,
        transactionCount: data?.transactionCount || 3,
      };

      setReconcilingStatement(newStatement);
      setIsUploadModalOpen(false);
      notify.confirm('Statement imported successfully');

      // Create dummy lines for this statement
      const dummyLines = [
        { date: new Date('2026-03-01'), description: 'Starbucks Coffee', amount: -12.50, payee: 'Starbucks' },
        { date: new Date('2026-03-02'), description: 'INV-100 Payment', amount: 1500.00, payee: 'Client A' },
        { date: new Date('2026-03-03'), description: 'Rent March', amount: -2000.00, payee: 'Landlord' },
      ];

      for (const line of dummyLines) {
        await addDoc(collection(db, 'bank_statement_lines'), {
          orgId,
          statementId: stmtRef.id,
          accountId: selectedAccount.id,
          date: Timestamp.fromDate(line.date),
          amount: line.amount,
          payee: line.payee,
          description: line.description,
          status: 'unreconciled'
        });
      }

      notify.confirm('Bank statement imported successfully');
      setIsUploadModalOpen(false);
      setReconcilingStatement(newStatement);
    } catch (error) {
      console.error('Import error:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to import statement');
    } finally {
      setIsImporting(false);
    }
  };

  const handleMatchStatementLine = async (journalLineId: string) => {
    if (!selectedStatementLineId || !reconcilingStatement || !isLocalSession()) return;
    try {
      const response = await fetch(
        `/api/organizations/${orgId}/bank-statement-lines/${selectedStatementLineId}/match`,
        {
          method: 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify({ journalLineId })
        }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || `Failed to match statement line: ${response.status}`);
      const matchedLine = payload?.line as BankStatementLine;
      const statement = payload?.statement as BankStatement;
      setStatementLines(current => current.map(line => line.id === matchedLine.id ? matchedLine : line));
      if (statement?.id) {
        setReconcilingStatement(statement);
        setStatements(current => current.map(item => item.id === statement.id ? statement : item));
      }
      setSelectedStatementLineId(null);
      notify.confirm('Statement line reconciled');
    } catch (error) {
      console.error('Local API error matching statement line:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to reconcile statement line');
    }
  };

  const handleUnmatchStatementLine = async (lineId: string) => {
    if (!reconcilingStatement || !isLocalSession()) return;
    try {
      const response = await fetch(`/api/organizations/${orgId}/bank-statement-lines/${lineId}/unmatch`, {
        method: 'POST',
        headers: getLocalApiHeaders()
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || `Failed to unmatch statement line: ${response.status}`);
      const unmatchedLine = payload?.line as BankStatementLine;
      const statement = payload?.statement as BankStatement;
      setStatementLines(current => current.map(line => line.id === unmatchedLine.id ? unmatchedLine : line));
      if (statement?.id) {
        setReconcilingStatement(statement);
        setStatements(current => current.map(item => item.id === statement.id ? statement : item));
      }
      notify.confirm('Statement line marked unreconciled');
    } catch (error) {
      console.error('Local API error unmatching statement line:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to unmatch statement line');
    }
  };

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isLocalSession()) {
        const endpoint = editingAccount
          ? `/api/organizations/${orgId}/bank-accounts/${editingAccount.id}`
          : `/api/organizations/${orgId}/bank-accounts`;
        const response = await fetch(endpoint, {
          method: editingAccount ? 'PATCH' : 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify({
            ...formData,
            chartAccountId: editingAccount?.chartAccountId
          })
        });
        if (!response.ok) {
          throw new Error(`Failed to save bank account: ${response.status}`);
        }

        const savedAccount = await response.json() as BankAccount;
        setAccounts(currentAccounts => editingAccount
          ? currentAccounts.map(account => account.id === savedAccount.id ? savedAccount : account)
          : [savedAccount, ...currentAccounts]
        );
        notify.confirm(editingAccount ? 'Bank account updated successfully' : 'Bank account created successfully');
      } else if (editingAccount) {
        await updateDoc(doc(db, 'bank_accounts', editingAccount.id), {
          ...formData,
          updatedAt: serverTimestamp()
        });
        
        // Also try to update corresponding COA if it exists
        const coaQuery = query(
          collection(db, 'chart_of_accounts'),
          where('orgId', '==', orgId),
          where('name', '==', editingAccount.accountName),
          where('type', '==', 'Bank')
        );
        const coaSnap = await getDocs(coaQuery);
        if (!coaSnap.empty) {
          await updateDoc(doc(db, 'chart_of_accounts', coaSnap.docs[0].id), {
            name: formData.accountName,
            nature: formData.nature,
            ytd: formData.balance,
            currency: formData.currency,
            updatedAt: serverTimestamp()
          });
        }

        notify.confirm('Bank account updated successfully');
      } else {
        // 1. Create bank account record
        const bankDoc = await addDoc(collection(db, 'bank_accounts'), {
          ...formData,
          orgId,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid || 'unknown'
        });

        // 2. Create corresponding Chart of Accounts entry
        const userName = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Unknown User';
        const userEmail = auth.currentUser?.email || 'No Email';
        
        await addDoc(collection(db, 'chart_of_accounts'), {
          code: formData.accountNumber || `BANK-${Math.floor(1000 + Math.random() * 9000)}`,
          name: formData.accountName,
          type: 'Bank',
          nature: formData.nature,
          description: `Bank account: ${formData.bankName}`,
          taxRate: '0%',
          ytd: formData.balance,
          status: 'active',
          orgId,
          createdBy: auth.currentUser?.uid || 'unknown',
          createdByName: userName,
          createdByEmail: userEmail,
          createdAt: serverTimestamp(),
          isSystemAccount: false,
          bankAccountId: bankDoc.id, // Link them
          actionLog: [
            {
              type: 'create',
              by: `${userName} (${userEmail})`,
              at: new Date().toISOString()
            }
          ]
        });

        notify.confirm('Bank account created successfully');
      }
      setIsModalOpen(false);
      setEditingAccount(null);
      setFormData({
        accountName: '',
        accountNumber: '',
        bankName: '',
        balance: 0,
        currency: systemSettings.defaultCurrency,
        nature: 'Debit'
      });
    } catch (error) {
      if (isLocalSession()) {
        console.error('Local API error saving bank account:', error);
      } else {
        handleFirestoreError(error, editingAccount ? OperationType.UPDATE : OperationType.CREATE, 'bank_accounts');
      }
      notify.error('Failed to save bank account');
    }
  };

  const handleEditClick = (account: BankAccount) => {
    setEditingAccount(account);
    setFormData({
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      bankName: account.bankName,
      balance: account.balance,
      currency: account.currency,
      nature: account.nature || 'Debit'
    });
    setIsModalOpen(true);
  };

  const handleDeleteAccount = async (id: string) => {
    try {
      if (isLocalSession()) {
        const response = await fetch(`/api/organizations/${orgId}/bank-accounts/${id}`, {
          method: 'DELETE',
          headers: getLocalApiHeaders()
        });
        if (!response.ok) {
          throw new Error(`Failed to delete bank account: ${response.status}`);
        }

        setAccounts(currentAccounts => currentAccounts.filter(account => account.id !== id));
      } else {
        await deleteDoc(doc(db, 'bank_accounts', id));
      }
      notify.confirm('Bank account deleted successfully');
    } catch (error) {
      if (isLocalSession()) {
        console.error('Local API error deleting bank account:', error);
      } else {
        handleFirestoreError(error, OperationType.DELETE, 'bank_accounts');
      }
      notify.error('Failed to delete bank account');
    }
  };

  const filteredAccounts = accounts.filter(acc => 
    acc.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.bankName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const selectedStatementLine = statementLines.find(line => line.id === selectedStatementLineId) || null;
  const matchingTransactions = selectedStatementLine
    ? accountTransactions.filter(transaction => Math.abs(transaction.amount - selectedStatementLine.amount) <= 0.01)
    : accountTransactions;
  const reconciledLineCount = statementLines.filter(line => line.status === 'reconciled').length;

  if (selectedAccount) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-border bg-card px-5 py-4 shadow-sm">
          <div className="flex items-start gap-3">
            <button 
              onClick={() => {
                setSelectedAccount(null);
                onClearInitial?.();
              }}
              className="mt-1 rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Back to bank accounts"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cash & Bank / Bank accounts</p>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">{selectedAccount.accountName}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{selectedAccount.bankName} · **** {selectedAccount.accountNumber.slice(-4)}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current balance</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
              {selectedAccount.currency} {selectedAccount.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto border-b border-border">
          <button 
            onClick={() => setActiveTab('reconciliation')}
            className={cn(
              "whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-all",
              activeTab === 'reconciliation' ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Reconciliation
          </button>
          <button 
            onClick={() => setActiveTab('transactions')}
            className={cn(
              "whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-all",
              activeTab === 'transactions' ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Transactions
          </button>
          <button 
            onClick={() => setActiveTab('details')}
            className={cn(
              "whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-all",
              activeTab === 'details' ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Account Details
          </button>
        </div>

        {activeTab === 'transactions' && (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Description</th>
                    <th className="px-6 py-4 text-right">Receive</th>
                    <th className="px-6 py-4 text-right">Spend</th>
                    <th className="px-6 py-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {accountTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center opacity-50 italic serif">
                        No transactions found for this account
                      </td>
                    </tr>
                  ) : (
                    (accountTransactions || []).map((tx) => (
                      <tr key={tx.id} className="group transition-colors hover:bg-muted/50">
                        <td className="px-6 py-4 text-sm font-mono">{new Date(tx.date).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-bold">{tx.description}</p>
                          <p className="text-[10px] opacity-40 uppercase tracking-wider">{tx.category || 'Uncategorized'}</p>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-green-600">
                          {tx.amount > 0 ? `${selectedAccount.currency} ${tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-red-600">
                          {tx.amount < 0 ? `${selectedAccount.currency} ${Math.abs(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={cn(
                            "text-[10px] uppercase font-bold px-2 py-1 rounded-full",
                            tx.status === 'Completed' ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                          )}>
                            {tx.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'reconciliation' && (
          <div className="space-y-5">
            {!reconcilingStatement ? (
              <div className="space-y-12">
                <div className="flex items-center justify-center py-12">
                  <div className="max-w-md w-full text-center space-y-8">
                    <div className="w-24 h-24 bg-[#141414]/5 rounded-[32px] flex items-center justify-center mx-auto">
                      <FileUp className="w-10 h-10 opacity-40" />
                    </div>
                    <div>
                      <h2 className="mb-2 text-2xl font-bold tracking-tight text-foreground">Bank reconciliation</h2>
                      <p className="text-sm text-muted-foreground">
                        Match your bank feed to the transactions in your ledger and keep your account up to date.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <button 
                        onClick={() => setIsUploadModalOpen(true)}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
                      >
                        <FileUp className="w-5 h-5" />
                        <span>Import Statement</span>
                      </button>
                      <button 
                        onClick={handleDownloadTemplate}
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-border py-3 font-bold text-foreground transition-colors hover:bg-muted"
                      >
                        <Download className="w-5 h-5" />
                        <span>Download CSV Template</span>
                      </button>
                    </div>
                    <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">
                      {isLocalSession() ? 'Local mode supports CSV statement imports' : 'Supports OFX, QFX, QIF, QBO, and CSV'}
                    </p>
                    {statements.length > 0 && (
                      <div className="w-full text-left border-t border-[#141414]/10 pt-6 space-y-2">
                        <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Imported statements</p>
                        {statements.map(statement => (
                          <button
                            key={statement.id}
                            type="button"
                            onClick={() => {
                              setReconcilingStatement(statement);
                              setSelectedStatementLineId(null);
                            }}
                            className="w-full flex items-center justify-between gap-3 p-3 bg-white/50 border border-[#141414]/10 rounded-xl hover:border-[#141414] transition-colors"
                          >
                            <span className="min-w-0">
                              <span className="block text-xs font-bold truncate">{statement.fileName}</span>
                              <span className="block text-[10px] opacity-50">
                                {statement.importDate?.toDate
                                  ? statement.importDate.toDate().toLocaleDateString()
                                  : new Date(statement.importDate).toLocaleDateString()}
                              </span>
                            </span>
                            <span className={cn(
                              'text-[9px] uppercase font-bold px-2 py-1 rounded-full',
                              statement.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            )}>
                              {statement.matchedCount || 0}/{statement.transactionCount}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => setReconcilingStatement(null)}
                      className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h2 className="text-xl font-bold tracking-tight">Reconciling: {reconcilingStatement.fileName}</h2>
                      <p className="text-sm opacity-50 italic serif">Match bank statement lines (left) with system transactions (right)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Progress</p>
                      <p className="text-sm font-bold">{reconciledLineCount} / {reconcilingStatement.transactionCount} matched</p>
                    </div>
                    <button 
                      onClick={() => setReconcilingStatement(null)}
                      className="bg-[#141414] text-[#E4E3E0] px-6 py-2 rounded-full font-bold text-sm"
                    >
                      Save & Close
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {/* Left Side: Bank Statement Lines */}
                  <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Bank Statement Lines</h3>
                      <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-full uppercase">Imported</span>
                    </div>
                    
                    <div className="space-y-4">
                      {statementLines.length === 0 ? (
                        <div className="text-center py-12 opacity-40 italic serif">
                          No lines found for this statement
                        </div>
                      ) : (
                        (statementLines || []).map((line) => (
                          <div
                            key={line.id}
                            onClick={() => line.status === 'unreconciled' && setSelectedStatementLineId(line.id)}
                            className={cn(
                              "bg-white/50 border p-6 rounded-2xl hover:shadow-md transition-all cursor-pointer group",
                              line.status === 'reconciled' ? "border-green-500/30 opacity-60" : "border-[#141414]",
                              selectedStatementLineId === line.id && "ring-2 ring-[#141414]"
                            )}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-xs font-mono opacity-50 mb-1">
                                  {line.date ? new Date(`${line.date}T00:00:00`).toLocaleDateString() : 'N/A'}
                                </p>
                                <p className="font-bold">{line.description}</p>
                                <p className="text-[10px] opacity-40 uppercase tracking-wider mt-1">{line.payee}</p>
                              </div>
                              <div className="text-right">
                                <div className="flex gap-4">
                                  {line.amount < 0 && (
                                    <div>
                                      <p className="text-[10px] uppercase font-bold opacity-40">Spend</p>
                                      <p className="text-lg font-bold font-mono tracking-tighter text-red-600">
                                        {selectedAccount.currency} {Math.abs(line.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </p>
                                    </div>
                                  )}
                                  {line.amount > 0 && (
                                    <div>
                                      <p className="text-[10px] uppercase font-bold opacity-40">Receive</p>
                                      <p className="text-lg font-bold font-mono tracking-tighter text-green-600">
                                        {selectedAccount.currency} {line.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </p>
                                    </div>
                                  )}
                                </div>
                                {line.status === 'reconciled' && (
                                  <div className="mt-2 flex justify-end gap-2">
                                    <span className="text-[8px] uppercase font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded inline-block">Reconciled</span>
                                    {isLocalSession() && (
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void handleUnmatchStatementLine(line.id);
                                        }}
                                        className="text-[8px] uppercase font-bold text-red-600 hover:underline"
                                      >
                                        Unmatch
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Right Side: System Transactions */}
                  <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">System Transactions</h3>
                      <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-1 rounded-full uppercase">Ledger</span>
                    </div>

                    <div className="space-y-4">
                      {matchingTransactions.length === 0 ? (
                        <div className="text-center py-12 opacity-40 italic serif">
                          {selectedStatementLine ? 'No posted ledger transaction has the same amount.' : 'Select a statement line to find a match.'}
                        </div>
                      ) : (
                        (matchingTransactions || []).slice(0, 10).map((tx) => (
                          <div key={tx.id} className="bg-white/80 border border-[#141414]/10 p-6 rounded-2xl hover:border-[#141414] transition-all group flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-xs font-mono opacity-50">{new Date(tx.date).toLocaleDateString()}</p>
                                <span className="text-[8px] uppercase font-bold bg-[#141414]/5 px-1.5 py-0.5 rounded">Match Found</span>
                              </div>
                              <p className="font-bold">{tx.description}</p>
                              <p className="text-[10px] opacity-40 uppercase tracking-wider">{tx.category}</p>
                            </div>
                            <div className="text-right flex items-center gap-6">
                              <div>
                                <p className={cn(
                                  "text-lg font-bold font-mono tracking-tighter",
                                  tx.amount > 0 ? "text-green-600" : "text-red-600"
                                )}>
                                  {selectedAccount.currency} {Math.abs(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </p>
                              </div>
                              <button
                                type="button"
                                disabled={!selectedStatementLine || !tx.matchable || !isLocalSession()}
                                onClick={() => void handleMatchStatementLine(tx.id)}
                                className="bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-full text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-30"
                              >
                                Match
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                      
                      <div className="pt-6 border-t border-[#141414]/10">
                        <button className="w-full py-4 border-2 border-dashed border-[#141414]/20 rounded-2xl text-sm font-bold opacity-40 hover:opacity-100 hover:border-[#141414] transition-all">
                          + Create New Transaction to Match
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'details' && (
          <div className="max-w-2xl rounded-xl border border-border bg-card p-6 shadow-sm">
            <h2 className="text-xl font-bold tracking-tight mb-8">Account Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Account Name</p>
                <p className="font-bold">{selectedAccount.accountName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Bank Name</p>
                <p className="font-bold">{selectedAccount.bankName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Account Number</p>
                <p className="font-mono font-bold">**** {selectedAccount.accountNumber}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Currency</p>
                <p className="font-bold">{selectedAccount.currency}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Source</p>
                <p className="font-bold uppercase text-xs">{String(selectedAccount?.source || '').replace('_', ' ')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">Created At</p>
                <p className="font-bold">{selectedAccount.createdAt?.toDate ? selectedAccount.createdAt.toDate().toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
            
            <div className="mt-12 pt-8 border-t border-[#141414]/10 flex gap-4">
              <button 
                onClick={() => handleEditClick(selectedAccount)}
                className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit Account</span>
              </button>
              <button 
                onClick={() => setDeletingAccountId(selectedAccount.id)}
                className="flex items-center gap-2 border border-red-500 text-red-500 px-6 py-3 rounded-full hover:bg-red-500 hover:text-white transition-all font-bold"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Account</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bank Accounts</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Manage your business bank accounts and balances</p>
        </div>
        <button 
          onClick={() => {
            setEditingAccount(null);
            setFormData({
              accountName: '',
              accountNumber: '',
              bankName: '',
              balance: 0,
              currency: systemSettings.defaultCurrency,
              nature: 'Debit'
            });
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
        >
          <Plus className="w-4 h-4" />
          <span>Add Bank Account</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 bg-white/50 border border-[#141414] px-4 py-3 rounded-2xl">
        <Search className="w-5 h-5 opacity-50" />
        <input 
          type="text" 
          placeholder="Search by account name or bank..." 
          className="bg-transparent border-none outline-none text-sm w-full"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading accounts...</p>
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <CreditCard className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No bank accounts found</p>
          <p className="text-sm opacity-30 mt-2">Add your first account to start tracking balances</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAccounts.map((account) => (
            <div 
              key={account.id} 
              onClick={() => setSelectedAccount(account)}
              className="bg-white/50 border border-[#141414] p-6 rounded-3xl hover:shadow-xl transition-all group relative cursor-pointer"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="p-3 bg-[#141414] text-[#E4E3E0] rounded-2xl">
                  <CreditCard className="w-6 h-6" />
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(account);
                    }}
                    className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingAccountId(account.id);
                    }}
                    className="p-2 hover:bg-red-500 hover:text-white rounded-full transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold tracking-tight">{account.accountName}</h3>
                <p className="text-sm opacity-50 italic serif">{account.bankName}</p>
                <p className="text-xs font-mono mt-1 opacity-40">**** {account.accountNumber.slice(-4)}</p>
              </div>

              <div className="mt-8 pt-6 border-t border-[#141414]/10 flex items-end justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold opacity-50">Current Balance</p>
                  <p className="text-2xl font-bold font-mono tracking-tighter">
                    {account.currency} {account.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={cn(
                  "flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full",
                  account.balance >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                )}>
                  {account.balance >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                  {account.balance >= 0 ? 'Healthy' : 'Overdrawn'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal 
        isOpen={!!deletingAccountId}
        onClose={() => setDeletingAccountId(null)}
        onConfirm={() => {
          if (deletingAccountId) {
            handleDeleteAccount(deletingAccountId);
            if (selectedAccount?.id === deletingAccountId) {
              setSelectedAccount(null);
            }
          }
        }}
        title="Delete Bank Account"
        message="Are you sure you want to delete this bank account? This action cannot be undone and all transaction history for this account will be affected."
        confirmText="Delete"
      />

      {/* Add Account Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsModalOpen(false);
              setEditingAccount(null);
            }
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold tracking-tight">
                {editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
              </h2>
              <button 
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingAccount(null);
                }}
                className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddAccount} className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Account Name</label>
                <input 
                  required
                  type="text" 
                  className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                  placeholder="e.g. Business Checking"
                  value={formData.accountName}
                  onChange={(e) => setFormData({...formData, accountName: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Bank Name</label>
                <input 
                  required
                  type="text" 
                  className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                  placeholder="e.g. Chase Bank"
                  value={formData.bankName}
                  onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">Account Number</label>
                  <input 
                    required
                    type="text" 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                    placeholder="Last 4 digits"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">Account Nature</label>
                  <select 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                    value={formData.nature}
                    onChange={(e) => setFormData({...formData, nature: e.target.value as any})}
                  >
                    <option value="Debit">Debit</option>
                    <option value="Credit">Credit</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">Currency</label>
                  <select 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  >
                    {[systemSettings.defaultCurrency, ...systemSettings.allowedCurrencies]
                      .filter((curr, index, self) => curr && self.indexOf(curr) === index)
                      .map(curr => (
                        <option key={curr} value={curr}>{curr}</option>
                      ))
                    }
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">
                    {editingAccount ? 'Current Balance' : 'Initial Balance'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold opacity-50 text-xs">{formData.currency}</span>
                    <input 
                      required
                      type="number" 
                      step="0.01"
                      className="w-full bg-white/50 border border-[#141414] p-4 pl-14 rounded-2xl outline-none focus:bg-white transition-all font-mono"
                      placeholder="0.00"
                      value={isNaN(formData.balance) ? '' : formData.balance}
                      onChange={(e) => setFormData({...formData, balance: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
              </div>

              <button 
                type="submit"
                className="w-full bg-[#141414] text-[#E4E3E0] py-4 rounded-full font-bold hover:opacity-90 transition-opacity mt-4"
              >
                {editingAccount ? 'Update Account' : 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Bank Statement Upload Modal */}
      {isUploadModalOpen && selectedAccount && (
        <BankStatementUpload 
          onClose={() => setIsUploadModalOpen(false)}
          onComplete={handleImportStatement}
          accountName={selectedAccount.accountName}
          currency={selectedAccount.currency}
          lastTransaction={accountTransactions[0] ? {
            date: new Date(accountTransactions[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            description: accountTransactions[0].description,
            amount: accountTransactions[0].amount
          } : undefined}
        />
      )}
    </div>
  );
}
